import {
  Component,
  Inject,
  HostListener,
} from "@angular/core";
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { DatePipe } from "@angular/common";
import { isVideo, isAudio, isDocument } from "./utils/helpers.utils";
import { downloadFile } from "../helpers/helper";

type ImageFile = {
  fileUrl: string;
  url?: string;
  name?: string;
  senderName?: string;
  date?: any;
  message?: string;
  profileImg?: string;
  thumbnailUrl?: string;
  type?: string;
};

@Component({
  selector: "app-image-viewer-dialog",
  imports: [MatDialogModule, DatePipe],
  template: `
<div class="image-dialog">
  
  <div class="dialog-backdrop" (click)="dialogRef.close()"></div>

  <!-- Top Header with Sender Info -->
  <div class="viewer-header">
    <div class="header-left">
      <button class="back-btn-dialog" (click)="dialogRef.close()" title="Back to results">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </button>

      @if (currentFile?.senderName && currentFile?.senderName !== 'User' && currentFile?.senderName !== 'Unknown') {
        <div class="sender-info">
          <div class="sender-avatar">
            @if (currentFile?.profileImg) {
              <img [src]="currentFile?.profileImg" alt="avatar" />
            } @else {
              <div class="avatar-placeholder">{{ (currentFile?.senderName || 'U')[0].toUpperCase() }}</div>
            }
          </div>
          <div class="sender-meta">
            <span class="sender-name">{{ currentFile?.senderName }}</span>
            <span class="date">{{ currentFile?.date | date: 'MMM d, y, hh:mma' }}</span>
          </div>
        </div>
      }
    </div>

    <!-- Top Right Actions -->
    <div class="dialog-actions">
      <div
        class="action-btn download-btn"
        (click)="downloadFile(); $event.stopPropagation()"
        title="Download file"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
      </div>
      <button
        class="action-btn close-btn"
        (click)="dialogRef.close(); $event.stopPropagation()"
        title="Close"
      >
        ✕
      </button>
    </div>
  </div>

  <div class="image-stage-container">
    @if (files.length > 1) {
      <button class="nav-btn prev-btn" (click)="prev(); $event.stopPropagation()" aria-label="Previous">
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <button class="nav-btn next-btn" (click)="next(); $event.stopPropagation()" aria-label="Next">
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    }

    <div class="image-stage" (click)="$event.stopPropagation()">
      @if(isVideoType(currentFile)) {
        <video
          [src]="currentFile?.fileUrl || currentFile?.url"
          [poster]="currentFile?.thumbnailUrl || ''"
          controls
          autoplay
          class="image-display"
        ></video>
      } @else if (isAudioType(currentFile)) {
        <div class="file-preview audio-preview">
          <img src="assets/audio-thumbnail.png" class="file-icon" />
          <p class="file-name">{{ currentFile?.name }}</p>
          <audio
            [src]="currentFile?.fileUrl || currentFile?.url"
            controls
          ></audio>
        </div>
      } @else if (isDocumentType(currentFile)) {
        <div class="file-preview document-preview">
          <img src="assets/paper.png" class="file-icon" />
          <p class="file-name">{{ currentFile?.name }}</p>
        </div>
      } @else {
        <img
          [src]="currentFile?.fileUrl || currentFile?.url"
          [alt]="currentFile?.name || 'Image'"
          class="image-display"
        />
      }
    </div>
  </div>

  <div class="dialog-footer">
    @if (currentFile?.message && !isDataId(currentFile.message)) {
      <div class="message-caption">
        {{ currentFile?.message }}
      </div>
    }
    @if (files.length > 1) {
      <span class="pager">{{ currentIndex + 1 }} / {{ files.length }}</span>
    }
  </div>
</div>
`,
  styles: [
    `
      :host {
        display: block;
        width: 100vw;
        height: 100vh;
        max-width: 100vw !important;
        max-height: 100vh !important;
        margin: 0 !important;
        overflow: hidden;
        background: transparent;
      }

      .image-dialog {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        background: rgba(0, 0, 0, 0.95);
        backdrop-filter: blur(10px);
        overflow: hidden;
      }

      .dialog-backdrop {
        position: absolute;
        inset: 0;
        z-index: 0;
      }

      .viewer-header {
        height: 70px;
        background: rgba(0,0,0,0.6);
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 20px;
        z-index: 10;
        border-bottom: 1px solid rgba(255,255,255,0.05);
      }

      .header-left {
        display: flex;
        align-items: center;
        gap: 16px;
      }

      .back-btn-dialog {
        background: transparent;
        border: none;
        color: white;
        cursor: pointer;
        padding: 8px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
      }

      .back-btn-dialog:hover {
        background: rgba(255,255,255,0.1);
      }

      .sender-info {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .sender-avatar {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        overflow: hidden;
        border: 2px solid rgba(255,255,255,0.2);
        background: #4d88ff;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .sender-avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .avatar-placeholder {
        color: white;
        font-weight: 600;
        font-size: 1.2rem;
      }

      .sender-meta {
        display: flex;
        flex-direction: column;
      }

      .sender-name {
        color: white;
        font-weight: 500;
        font-size: 1.05rem;
      }

      .date {
        color: rgba(255,255,255,0.6);
        font-size: 0.8rem;
      }

      .dialog-actions {
        display: flex;
        gap: 16px;
      }

      .action-btn {
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: #fff;
        width: 44px;
        height: 44px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
      }

      .action-btn:hover {
        background: rgba(255, 255, 255, 0.18);
        transform: scale(1.05);
      }

      .close-btn { font-size: 20px; }

      .image-stage-container {
        position: relative;
        flex: 1;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        z-index: 1;
      }

      .image-stage {
        max-width: 90%;
        max-height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .image-display {
        max-width: 100%;
        max-height: 80vh;
        object-fit: contain;
        border-radius: 4px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
      }

      .file-preview {
        display: flex;
        flex-direction: column;
        align-items: center;
        background: rgba(255, 255, 255, 0.04);
        padding: 50px;
        border-radius: 20px;
        border: 1px solid rgba(255, 255, 255, 0.08);
      }

      .file-icon {
        width: 150px;
        height: 150px;
        margin-bottom: 24px;
        filter: drop-shadow(0 8px 16px rgba(0,0,0,0.4));
      }

      .file-name {
        color: #fff;
        font-size: 1.1rem;
        margin-bottom: 24px;
        text-align: center;
        max-width: 350px;
      }

      .nav-btn {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: white;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 5;
        transition: all 0.25s ease;
      }

      .nav-btn:hover {
        background: rgba(255, 255, 255, 0.15);
        transform: translateY(-50%) scale(1.1);
      }

      .prev-btn { left: 40px; }
      .next-btn { right: 40px; }

      .dialog-footer {
        width: 100%;
        background: rgba(0,0,0,0.7);
        padding: 20px 40px;
        min-height: 80px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        z-index: 10;
        border-top: 1px solid rgba(255,255,255,0.1);
      }

      .message-caption {
        color: white;
        font-size: 1rem;
        max-width: 800px;
        text-align: center;
        line-height: 1.5;
        font-weight: 400;
        letter-spacing: 0.2px;
      }

      .pager {
        background: rgba(255, 255, 255, 0.1);
        padding: 4px 14px;
        border-radius: 14px;
        color: rgba(255,255,255,0.8);
        font-size: 0.85rem;
      }

      @media (max-width: 768px) {
        .nav-btn {
          width: 44px;
          height: 44px;
          background: rgba(0,0,0,0.5);
        }
        .prev-btn { left: 10px; }
        .next-btn { right: 10px; }
        .viewer-header { padding: 0 16px; }
        .dialog-footer { padding: 16px 20px; }
        .image-display { max-height: 70vh; }
      }
     
      @media (max-width: 580px) {
       .sender-name{
       width: 150px;
       text-overflow: ellipsis;
       white-space: nowrap;
       overflow: hidden;
       font-size:14px;
       }
      .back-btn-dialog{
       padding: 5px;
       }
      .back-btn-dialog svg{
        width: 22px;
        height: 22px;
    }
        .header-left ,  .dialog-actions{
          gap: 10px!important;
        }
        .sender-avatar{
          width: 40px!important;
          height: 40px!important;
        }
          .action-btn{
            width: 40px!important;
            height: 40px!important;
          }
            .action-btn.close-btn{
             font-size: 16px!important;
            }
      }

    `,
  ],
})
export class ImageViewerDialogComponent {
  files: ImageFile[] = [];
  currentIndex = 0;

  constructor(
    public dialogRef: MatDialogRef<ImageViewerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { files: ImageFile[]; startIndex: number }
  ) {
    this.files = data.files || [];
    this.currentIndex = data.startIndex || 0;
  }

  get currentFile(): ImageFile | null {
    return this.files[this.currentIndex] || null;
  }

  isVideoType(file: ImageFile | null): boolean {
    if (!file) return false;
    if (file.type?.startsWith('video/')) return true;
    return isVideo(file.fileUrl || file.url);
  }

  prev(): void {
    if (this.files.length === 0) return;
    this.currentIndex =
      (this.currentIndex - 1 + this.files.length) % this.files.length;
  }

  isAudioType(file: ImageFile | null): boolean {
    if (!file) return false;
    if (file.type?.startsWith('audio/')) return true;
    return isAudio(file.fileUrl || file.url);
  }

  isDocumentType(file: ImageFile | null): boolean {
    if (!file) return false;
    if (file.type?.startsWith('application/') || file.type?.includes('pdf') || file.type?.includes('word')) return true;
    return isDocument(file.fileUrl || file.url);
  }

  isDownloadable(url: string | any): boolean {
    return true; // Now everything is downloadable
  }

  downloadFile(): void {
    const url = this.currentFile?.fileUrl || this.currentFile?.url;
    downloadFile(url, this.currentFile?.name);
  }

  next(): void {
    if (this.files.length === 0) return;
    this.currentIndex = (this.currentIndex + 1) % this.files.length;
  }

  isDataId(text: string): boolean {
    if (!text) return false;
    // Basic heuristic: if it looks like a long specific ID or filename (contains extensions or many hyphens/underscores)
    return /\.(jpg|png|gif|mp4|webm|pdf|doc|xls|ppt)$/i.test(text) || text.length > 30 && (text.includes('-') || text.includes('_'));
  }

  @HostListener('document:keydown.arrowLeft', ['$event'])
  onArrowLeft(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    this.prev();
  }

  @HostListener('document:keydown.arrowRight', ['$event'])
  onArrowRight(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    this.next();
  }
}
